import { useEffect, useState } from "react";
import { DOMAIN, getAccessToken } from "../constants";

/**
 * Deployment settings an administrator flips in Django admin.
 *
 * Fetched rather than built in, because one bundle is served to every
 * deployment: turning photo capture off for a client must not need a release.
 *
 * The defaults below are what an unreachable or older backend implies. They say
 * *on*, because the endpoint is new and every deployment that has not got it
 * yet should behave like the setting is at its default rather than lose the
 * feature to a 404.
 */
export type AppSettings = {
  photo_capture_enabled: boolean;
  photo_max_dimension: number;
};

export const DEFAULT_APP_SETTINGS: AppSettings = {
  photo_capture_enabled: true,
  photo_max_dimension: 1920,
};

// One request per page load, shared by every component that asks. The settings
// change about once a year; refetching them per Upload field would be silly.
let pending: Promise<AppSettings> | null = null;

export const fetchAppSettings = (): Promise<AppSettings> => {
  if (!pending) {
    pending = (async () => {
      try {
        const token = getAccessToken();
        const response = await fetch(`${DOMAIN}/api/main/app-settings/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) return DEFAULT_APP_SETTINGS;
        const body = await response.json();
        return { ...DEFAULT_APP_SETTINGS, ...body };
      } catch {
        return DEFAULT_APP_SETTINGS;
      }
    })();
  }
  return pending;
};

/** Forget the cached answer — used when a different user signs in. */
export const resetAppSettings = () => {
  pending = null;
};

export const useAppSettings = (): AppSettings => {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_APP_SETTINGS);
  useEffect(() => {
    let alive = true;
    fetchAppSettings().then((value) => {
      if (alive) setSettings(value);
    });
    return () => {
      alive = false;
    };
  }, []);
  return settings;
};
